'use client';

import type { Ref } from 'react';

import classnames from 'classnames';

import type { BaseComponentProps } from '../../base-component.types';
import { Icon } from '../../icon';
import type {
  SelectOption,
  SelectOptionLabelsType,
} from './SelectOption.types';

import styles from './SelectOptions.module.scss';

type ContainerProps = BaseComponentProps & {
  options: SelectOption[];
  labels?: SelectOptionLabelsType;
  onClick: (option: SelectOption) => void;
  onAddOption?: () => void;
  id?: string;
  role?: string;
  'aria-labelledby'?: string;
};

export function SelectOptions({
  className,
  options,
  labels = {
    empty: 'No options available',
    add: 'Add new option...',
  },
  onClick,
  onAddOption,
  testId = 'ui-select-options',
  id,
  role,
  'aria-labelledby': ariaLabelledBy,
  ref,
  ...rest
}: ContainerProps & { ref?: Ref<HTMLDivElement> }) {
  return (
    <div
      ref={ref}
      id={id}
      role={role}
      aria-labelledby={ariaLabelledBy}
      className={classnames(styles['main'], className)}
      data-testid={testId}
      {...rest}
    >
      {options.length === 0 && (
        <>
          <span
            className={classnames(styles['option'], styles['option--disabled'])}
            role="option"
            aria-disabled="true"
          >
            <span className={styles['option__label']}>{labels.empty}</span>
          </span>
          {onAddOption && (
            <span
              className={classnames(
                styles['option'],
                styles['option--add'],
                className,
              )}
              role="option"
              onClick={onAddOption}
            >
              <span className={styles['option__label']}>{labels.add}</span>
            </span>
          )}
        </>
      )}
      {options.map((option: SelectOption, index) => {
        return (
          <span
            className={classnames(
              styles['option'],
              option.disabled && styles['option--disabled'],
              !!option.customLabel && styles['option--customLabel'],
              className,
            )}
            key={option.value ?? index}
            role="option"
            aria-selected={option.selected}
            aria-disabled={option.disabled}
            onClick={() => {
              if (!option.disabled) {
                onClick(option);
              }
            }}
          >
            {option.icon && (
              <span>
                <Icon icon={option.icon} size="sm" />
              </span>
            )}
            <span className={styles['option__label']}>
              {option.customLabel ?? option.label}
            </span>
          </span>
        );
      })}
    </div>
  );
}
